<template>
  <div class="summary w-100 text-start p-1 mt-2">
    <div v-if="cartStore.cart.length > 0">
      <h5 class="bold">Summary</h5>

      <div class="d-flex flex-row mt-3">
        <div class="flex-grow-1 text-start">
          Total sum ({{
            cartStore.cart.length > 0 ? cartStore.cart.length : 0
          }}
          {{ cartStore.cart.length === 1 ? "product" : "products" }})
        </div>
        <div class="d-flex justify-content-end">
          {{
            cartStore.cart.length > 0
              ? formatCurrency(
                  cartStore.totalPrice,
                  cartStore.cart[0].product.currency
                )
              : 0
          }}
        </div>
      </div>

      <div
        v-if="cartStore.cart.length > 0 && cartStore.totalDiscount > 0"
        class="d-flex flex-row"
      >
        <div class="flex-grow-1 text-start">Total discount</div>
        <div class="d-flex justify-content-end">
          {{
            cartStore.cart.length > 0
              ? "-" +
                formatCurrency(
                  cartStore.totalDiscount,
                  cartStore.cart[0].product.currency
                )
              : 0
          }}
        </div>
      </div>

      <div class="d-flex flex-row mt-2 mb-4">
        <div class="flex-grow-1 text-start">
          <label class="h5 bold">Total to pay</label>
        </div>
        <div class="d-flex justify-content-end">
          <label class="h5 bold">{{
            cartStore.cart.length > 0
              ? formatCurrency(
                  cartStore.totalOrderPrice,
                  cartStore.cart[0].product.currency
                )
              : 0
          }}</label>
        </div>
      </div>

      <div class="mb-3 required-input-wrapper">
        <span class="required-asterisk">*</span>
        <input
          type="text"
          class="form-control"
          v-model="orderReference"
          placeholder="Order reference..."
        />
      </div>

      <div class="mb-3">
        <textarea
          class="form-control"
          v-model="orderMessage"
          placeholder="Optional message to us..."
          rows="3"
        ></textarea>
      </div>

      <div class="mb-3">
        <label class="h5 bold">
          <span class="text-danger">*</span>Delivery Address</label
        >

        <div class="dropdown">
          <button
            class="btn btn-secondary dropdown-toggle btn-dropdelivery"
            type="button"
            id="dropdownDelivery"
            data-bs-toggle="dropdown"
          >
            {{
              selectedDeliveryAddress
                ? `${selectedDeliveryAddress.addressCountryRegionId}, ${selectedDeliveryAddress.addressCity}`
                : "Select delivery address..."
            }}
          </button>
          <div class="dropdown-menu dropdown-menu-end p-2">
            <div
              v-for="value in deliveryAddresses"
              :key="value.addressZipCode"
              :value="value"
              class="row delivery-item"
              @click="selectDeliveryAddress(value)"
            >
              <div class="d-flex justify-content-between">
                <span class="country fw-bold"
                  >Country: {{ value.addressCountryRegionId || "" }}</span
                >
                <span class="city text-end fw-bold"
                  >City: {{ value.addressCity || "" }}</span
                >
              </div>

              <div class="col-12">
                <b>Address:</b> <br />
                {{ value.addressStreet || "" }}
                <br />
                {{ value.addressZipCode || "" }}

                <b>Description:</b> {{ value.addressDescription || "" }}
              </div>

              <hr class="delivery-divider" />
            </div>
            <div
              class="row delivery-item py-2"
              @click="selectedDeliveryAddress = undefined"
            >
              <div class="col-12">Leave empty</div>
            </div>
          </div>
        </div>
        <div v-if="selectedDeliveryAddress" class="delivery-selected-wrap">
          <div class="delivery-selected">
            <div class="d-flex justify-content-between">
              <span class="country fw-bold"
                >Country:
                {{ selectedDeliveryAddress.addressCountryRegionId || "" }}</span
              >
              <span class="city text-end fw-bold"
                >City: {{ selectedDeliveryAddress.addressCity || "" }}</span
              >
            </div>

            <div class="col-12">
              <b>Address:</b> <br />
              {{ selectedDeliveryAddress.addressStreet || "" }}
              <br />
              {{ selectedDeliveryAddress.addressZipCode || "" }}

              <b>Description:</b>
              {{ selectedDeliveryAddress.addressDescription || "" }}
            </div>
          </div>
        </div>
      </div>

      <div class="mb-3">
        <button
          :disabled="
            cartStore.cart.length === 0 ||
            isLoading ||
            cartStore.totalOrderPrice === 0
          "
          class="btn btn-primary w-100"
          v-on:click="sendOrder()"
        >
          {{ isLoading ? "Placing order..." : "Place order" }}
        </button>
        <Loading :isLoading="isLoading" />
      </div>
    </div>
    <div v-if="orderSent">
      <h5 class="bold">Order sent</h5>
      Your order is sent to us.<br /><br />
      An order confirmation should be sent to your mail within five working
      days.
      <br /><br />
      Thank you for your order.
      <br /><br />
      <button class="btn btn-primary" @click="$router.push('/')">
        Back to home
      </button>
      <br /><br />
      <br /><br />

      <br /><br />
      <br /><br />
      <br /><br />
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-facing-decorator";
import { useCartStore } from "@/store/cartStore";
import {
  OrderClient,
  CreateOrderRequest,
  OrderLines,
  ArtWork,
  GetDeliveryAddressRequest,
  DeliveryAddress,
} from "@/data/client";
import { useUserStore } from "@/store/userStore";
import { toast } from "vue3-toastify";
import "@vuepic/vue-datepicker/dist/main.css";
import Loading from "@/components/shared/Loading.vue";
import { formatCurrency } from "@/helpers/HelperTool";
import axios, { Axios } from "axios";
import { msalInstance } from "@/config/msalConfig";
import { msalService } from "@/config/useAuth";

@Component({ components: { Loading } })
class Summary extends Vue {
  cartStore = useCartStore();
  userStore = useUserStore();

  orderClient = new OrderClient();
  deliveryAddresses: DeliveryAddress[] = [];
  orderMessage!: string;
  orderReference!: string;
  orderSent = false;
  isLoading = false;

  selectedDeliveryAddress: DeliveryAddress | undefined = undefined;

  async mounted() {
    var axiosClient = axios.create();
    await this.initialize(axiosClient);

    this.orderClient = new OrderClient(
      process.env.VUE_APP_BACKEND_URI ?? "",
      axiosClient
    );

    const req = new GetDeliveryAddressRequest({
      customerAccountNumber:
        this.userStore.userInfo?.customerAccountNumber?.toString(),
    });
    this.deliveryAddresses = await this.orderClient
      .getDeliveryAdress(req)
      .then((response) => {
        return response;
      })
      .catch((error: Error) => {
        console.error("Error fetching delivery addresses:", error);
        return [];
      });
  }

  async initialize(axiosClient: Axios) {
    try {
      await msalInstance.initialize();
      this.registerAuthorizationHeaderInterceptor(axiosClient);
    } catch (error) {
      console.log("Initialization error", error);
    }
  }
  registerAuthorizationHeaderInterceptor =
    msalService().registerAuthorizationHeaderInterceptor;

  formatCurrency(value: number, currency: string | undefined = "USD"): string {
    return formatCurrency(value, currency);
  }

  selectDeliveryAddress(address: DeliveryAddress) {
    this.selectedDeliveryAddress = address;
  }

  sendOrder() {
    if (
      !this.selectedDeliveryAddress &&
      (!this.orderReference || this.orderReference.trim() === "")
    ) {
      toast.error(
        "Please enter an order reference and select a delivery address before placing your order.",
        {
          position: "top-center",
        }
      );
      return;
    }

    if (!this.orderReference || this.orderReference.trim() === "") {
      toast.error("Please enter order reference before placing your order.", {
        position: "top-center",
      });
      return;
    }

    if (!this.selectedDeliveryAddress) {
      toast.error(
        "Please select a delivery address before placing your order.",
        {
          position: "top-center",
        }
      );
      return;
    }

    this.isLoading = true;
    const order: OrderLines[] = [];

    this.cartStore.cart.map((item) => {
      const orderItem = new OrderLines({
        productNumber: Number(item.product.itemId),
        productName: item.product.name,
        quantity: item.quantity,
        price: item.price.toNumber(),
        discount: item.discount,
        requestedDeliveryDate: new Date(
          Date.UTC(
            item.requestedDeliveryDate?.getFullYear() ?? 0,
            item.requestedDeliveryDate?.getMonth() ?? 0,
            item.requestedDeliveryDate?.getDate() ?? 0
          )
        ),
        comment: item.comment,
        selectedArtWork: Object.entries(item.artWorkSelections)
          .filter(([key, artwork]) => artwork.id)
          .map(([key, artwork]) => new ArtWork({ part: key, id: artwork.id })),
      });
      order.push(orderItem);
    });

    const req = new CreateOrderRequest({
      customerAccountNumber: this.userStore.userInfo?.customerAccountNumber,
      firstName: this.userStore.userInfo?.firstName,
      lastName: this.userStore.userInfo?.lastName,
      currency: this.cartStore.cart[0].product.currency,
      totalAmount: this.cartStore.totalOrderPrice,
      orderDate: new Date(),
      accountId: this.userStore.userInfo?.accountId,
      contactId: this.userStore.userInfo?.contactId,
      message: this.orderMessage,
      orderReference: this.orderReference,
      deliveryAddress: this.selectedDeliveryAddress,
      orderLines: order,
    });

    this.orderClient
      .createOrder(req)
      .then(() => {
        this.isLoading = false;

        this.orderSent = true;

        this.cartStore.emptyCart();
        toast.success("Your order is sent!", {
          position: "top-center",
        });
      })
      .catch((error: Error) => {
        this.isLoading = false;
        console.error("Error placing order:", error);
      });
  }
}
export default Summary;
</script>
